import { Flex, Text } from '@chakra-ui/react';
import {
  IRoleType,
  IWorkPhase
} from '@publishing-engine/domain/models/workflow-phase-ownership';
import { ReactNode } from 'react';
import {
  assessmentPhaseDescription,
  IExpertPhases
} from 'src/components/Assessment/AssessmentPhase';

interface PhaseContent {
  heading: string | ((params: StringTemplateData) => string);
  description: string | ((params: StringTemplateData) => ReactNode);
}
type RoleContent = Record<IRoleType, PhaseContent>;

export type StringTemplateData = Record<string, any>;

type DocumentPhases = Record<IWorkPhase, RoleContent>;

const documentPhases: DocumentPhases = {
  assignment: {
    author: {
      heading: 'Assignment',
      description: 'Your manuscript is awaiting assignment to an editor.'
    },
    editorial: {
      heading: 'Assignment',
      description: 'Assign this Submission to an Editor.'
    },
    reviewer: {
      heading: 'Assignment',
      description: 'Waiting for an editor to assign this Submission.'
    },
    expert: { heading: '', description: '' }
  },
  initialAssessment: {
    author: {
      heading: 'Initial Assessment',
      description:
        "Your manuscript is currently undergoing initial assessment. The editor is reviewing its scope, quality, and completeness to ensure it aligns with the journal's standards. you will receive feedback soon with any necessary revisions or next steps."
    },
    editorial: {
      heading: 'Initial Assessment',
      description:
        "Ensure the manuscript meets the journal's standards and is ready for peer review by checking scope, quality, and completeness."
    },
    reviewer: {
      heading: 'Initial Assessment',
      description: ''
    },
    expert: {
      heading: 'Initial Assessment',
      description:
        'We invite you to assess this submission and provide an informed recommendation on its suitability. Your expertise is essential to guide editors in making well-informed decisions. Thank you for your thoughtful analysis and feedback.'
    }
  },
  peerReviewInvitation: {
    author: {
      heading: 'Peer Review',
      description:
        "Your manuscript is under peer review. Expert reviewers are evaluating its content, methodology, and relevance. Once the review is complete, you will receive their feedback and the editor's decision and comments."
    },
    editorial: {
      heading: 'Peer Review Invitation',
      description:
        'Invite Reviewers, manage their invitations and track Review statuses and deadlines.'
    },
    reviewer: {
      heading: 'Peer Review',
      description:
        "Your expertise is requested to review this submission. Please provide a comprehensive evaluation based on the quality, rigor, and relevance of the work. Your insights are crucial in ensuring the integrity and excellence of our academic community's publications. We value your thorough analysis and constructive feedback."
    },
    expert: {
      heading: 'Peer Review',
      description:
        "Your expertise is requested to review this submission. Please provide a comprehensive evaluation based on the quality, rigor, and relevance of the work. Your insights are crucial in ensuring the integrity and excellence of our academic community's publications. We value your thorough analysis and constructive feedback."
    }
  },
  peerReviewPending: {
    author: {
      heading: 'Peer Review',
      description:
        "Your manuscript is under peer review. Expert reviewers are evaluating its content, methodology, and relevance. Once the review is complete, you will receive their feedback and the editor's decision and comments."
    },
    editorial: {
      heading: 'Peer Review Pending',
      description:
        'Invite Reviewers, manage their invitations and track Review statuses and deadlines.'
    },
    reviewer: {
      heading: 'Peer Review',
      description:
        "Your expertise is requested to review this submission. Please provide a comprehensive evaluation based on the quality, rigor, and relevance of the work. Your insights are crucial in ensuring the integrity and excellence of our academic community's publications. We value your thorough analysis and constructive feedback."
    },
    expert: {
      heading: 'Peer Review',
      description:
        "Your expertise is requested to review this submission. Please provide a comprehensive evaluation based on the quality, rigor, and relevance of the work. Your insights are crucial in ensuring the integrity and excellence of our academic community's publications. We value your thorough analysis and constructive feedback."
    }
  },
  peerReviewFinished: {
    author: {
      heading: 'Peer Review',
      description:
        "Your manuscript is under peer review. Expert reviewers are evaluating its content, methodology, and relevance. Once the review is complete, you will receive their feedback and the editor's decision and comments."
    },
    editorial: {
      heading: 'Peer Review Decision',
      description:
        'The review target has been met and you can proceed to perform a decision based on reviewer feedback.'
    },
    reviewer: {
      heading: 'Peer Review',
      description:
        "Your expertise is requested to review this submission. Please provide a comprehensive evaluation based on the quality, rigor, and relevance of the work. Your insights are crucial in ensuring the integrity and excellence of our academic community's publications. We value your thorough analysis and constructive feedback."
    },
    expert: {
      heading: 'Peer Review',
      description: (params: StringTemplateData) =>
        `Your assessment was sent to the Editors on ${params.deadline}. You can only review the sent information.`
    }
  },
  revision: {
    author: {
      heading: 'Revision',
      description: (params: StringTemplateData) =>
        `Your manuscript requires a ${
          params.severity || 'minor'
        } Revision based on the Editor's and Reviewers' feedback. Please address the provided comments and resubmit your updated work for further evaluation.`
    },
    editorial: {
      heading: 'Revision',
      description: (params: StringTemplateData) =>
        `The author was invited to revise this Submission until ${params.deadline}. Updates to the submission are locked until the revision is finished.`
    },
    reviewer: {
      heading: 'Revision',
      description: (params: StringTemplateData) =>
        `Your review was sent to the editors${
          params?.dateSent ? ` on ${params.dateSent}` : ''
        }. You can only review the sent information, and if applicable, view this review's score or reasons for rescission.`
    },
    expert: {
      heading: 'Revision',
      description: (params: StringTemplateData) =>
        `Your assessment was sent to the Editors on ${params.deadline}. You can only review the sent information.`
    }
  },
  peerReview: {
    author: { heading: '', description: '' },
    editorial: { heading: '', description: '' },
    reviewer: { heading: '', description: '' },
    expert: { heading: '', description: '' }
  },
  preProduction: {
    author: {
      heading: 'Pre-Production',
      description:
        'Your manuscript has been provisionally accepted. The Editors are undertaking a comprehensive review to finalize your publication. This includes verification of files, agreements, and metadata. You will be contacted if any discrepancies or omissions are identified.'
    },
    editorial: {
      heading: 'Pre-Production',
      description:
        'This Submission is nearly ready to publish: review the checklist to complete any remaining tasks. If data is missing or unclear, you can request updates from the Author.'
    },
    reviewer: {
      heading: 'Pre-Production',
      description:
        'The editorial team has gathered enough feedback to advance this submission. No further action is required. We appreciate your time and effort!'
    },
    expert: { heading: 'Pre-Production', description: '' }
  },
  formalAcceptance: {
    author: {
      heading: 'Accepted for Publication',
      description: ''
    },
    editorial: {
      heading: 'Accepted for Publication',
      description: ''
    },
    reviewer: {
      heading: 'Accepted for Publication',
      description:
        'The editorial team has gathered enough feedback to advance this submission. No further action is required. We appreciate your time and effort!'
    },
    expert: { heading: '', description: '' }
  },
  revisionAssessment: {
    author: {
      heading: 'Revision Assessment',
      description: (params: StringTemplateData) =>
        `Your revision was sent to the editors on ${params.dateSent}. You can only review the sent information.`
    },
    editorial: {
      heading: 'Revision Assessment',
      description:
        'The author has submitted a revised Manuscript. Assess the changes made, and determine if further revisions are needed or if the Submission is ready for the next stage.'
    },
    reviewer: {
      heading: 'Rev - Assessment',
      description:
        "Your expertise is requested to review this revised submission. Please provide a comprehensive evaluation based on the quality, rigor, and relevance of the work. Your insights are crucial in ensuring the integrity and excellence of our academic community's publications. We value your thorough analysis and constructive feedback."
    },
    expert: {
      heading: 'Revision Assessment',
      description:
        "Your expertise is requested to review this submission. Please provide a comprehensive evaluation based on the quality, rigor, and relevance of the work. Your insights are crucial in ensuring the integrity and excellence of our academic community's publications. We value your thorough analysis and constructive feedback."
    }
  },
  revisionPeerReviewInvitation: {
    author: {
      heading: 'Revision Assessment',
      description:
        'Your revised manuscript is under peer review. Expert reviewers are evaluating the changes made in response to the previous feedback. Once the review is complete, you will receive the editor’s decision.'
    },
    editorial: {
      heading: 'Revision Peer Review Invitation',
      description:
        'Invite Reviewers to assess the revised manuscript, manage their invitations and track Review statuses and deadlines.'
    },
    reviewer: {
      heading: 'Rev - Assessment',
      description:
        'Your expertise is requested to review this revised submission. Please provide a comprehensive evaluation based on the quality, rigor, and relevance of the work. We value your thorough analysis and constructive feedback.'
    },
    expert: {
      heading: 'Peer Review',
      description:
        'Your expertise is requested to review this revised submission. Please provide a comprehensive evaluation based on the quality, rigor, and relevance of the work. We value your thorough analysis and constructive feedback.'
    }
  },
  revisionPeerReviewPending: {
    author: {
      heading: 'Revision Assessment',
      description:
        'Your revised manuscript is under peer review. Expert reviewers are evaluating the changes made in response to the previous feedback. Once the review is complete, you will receive the editor’s decision.'
    },
    editorial: {
      heading: 'Revision Peer Review Pending',
      description:
        'Invite Reviewers to assess the revised manuscript, manage their invitations and track Review statuses and deadlines.'
    },
    reviewer: {
      heading: 'Rev - Assessment',
      description:
        'Your expertise is requested to review this revised submission. Please provide a comprehensive evaluation based on the quality, rigor, and relevance of the work. We value your thorough analysis and constructive feedback.'
    },
    expert: {
      heading: 'Peer Review',
      description:
        'Your expertise is requested to review this revised submission. Please provide a comprehensive evaluation based on the quality, rigor, and relevance of the work. We value your thorough analysis and constructive feedback.'
    }
  },
  revisionPeerReviewFinished: {
    author: {
      heading: 'Revision Assessment',
      description:
        'Your revised manuscript is under peer review. Expert reviewers are evaluating the changes made in response to the previous feedback. Once the review is complete, you will receive the editor’s decision.'
    },
    editorial: {
      heading: 'Revision Peer Review Decision',
      description:
        'The review target has been met and you can proceed to perform a decision based on reviewer feedback.'
    },
    reviewer: {
      heading: 'Rev - Assessment',
      description:
        'Your expertise is requested to review this revised submission. Please provide a comprehensive evaluation based on the quality, rigor, and relevance of the work. We value your thorough analysis and constructive feedback.'
    },
    expert: {
      heading: 'Peer Review',
      description: (params: StringTemplateData) =>
        `Your assessment was sent to the Editors on ${params.deadline}. You can only review the sent information.`
    }
  },
  draft: {
    author: { heading: '', description: '' },
    editorial: { heading: '', description: '' },
    reviewer: { heading: '', description: '' },
    expert: { heading: '', description: '' }
  },
  immediateRevision: {
    author: {
      heading: 'Immediate Revision',
      description: (params: StringTemplateData) =>
        `Your manuscript requires a ${
          params.severity || 'minor'
        } Revision based on the Editor's and Reviewers' feedback. Please address the provided comments and resubmit your updated work for further evaluation.`
    },
    editorial: {
      heading: 'Immediate Revision',
      description: (params: StringTemplateData) =>
        `The author was invited to revise this Submission until ${params.deadline}. Updates to the submission are locked until the revision is finished.`
    },
    reviewer: {
      heading: 'Immediate Revision',
      description:
        "Your expertise is requested to review this submission. Please provide a comprehensive evaluation based on the quality, rigor, and relevance of the work. Your insights are crucial in ensuring the integrity and excellence of our academic community's publications. We value your thorough analysis and constructive feedback."
    },
    expert: { heading: '', description: '' }
  },
  transferOffered: {
    author: {
      heading: 'Transfer Offered',
      description: () => (
        <Flex flexDirection="column" gap={2}>
          <Text>
            Your Submission has been offered transfer to other Journals. You can
            review the journals, editorial feedback, and decide whether to
            accept the transfer.
          </Text>
          <Text>
            If you decline, or if you don’t decide before the deadline, this
            Submission will be rejected.
          </Text>
        </Flex>
      )
    },
    editorial: {
      heading: 'Transfer Offer',
      description: () => (
        <Flex flexDirection="column" gap={2}>
          <Text>
            This Submission has been rejected, and a transfer offer has been
            sent to the author via email. You may review the offer details or
            extend the deadline as needed.
          </Text>
          <Text>
            If the Author declines the transfer or does not respond by the
            deadline, the Submission will be formally rejected.
          </Text>
        </Flex>
      )
    },
    reviewer: {
      heading: 'Transfer Offer',
      description:
        'The editorial team has gathered enough feedback to advance this submission. No further action is required. We appreciate your time and effort!'
    },
    expert: { heading: 'Transfer Offer', description: '' }
  },
  transferPending: {
    author: { heading: '', description: '' },
    editorial: {
      heading: 'Incomplete Transfer',
      description: (params: StringTemplateData) =>
        `This submission has been accepted for transfer, but the author has not yet completed the required steps. The author was invited to complete this submission's transfer until ${params.deadline}. Updates to the submission are locked until the submission is complete.`
    },
    reviewer: {
      heading: 'Incomplete Transfer',
      description: (params: StringTemplateData) =>
        `This submission has been accepted for transfer, but the author has not yet completed the required steps. The author was invited to complete this submission's transfer until ${params.deadline}. Updates to the submission are locked until the submission is complete.`
    },
    expert: { heading: '', description: '' }
  },
  unsubmitted: {
    author: {
      heading: 'Unsubmitted',
      description: () => (
        <Flex flexDirection="column">
          <Text>
            This submission was unsubmitted and reverted to a draft state.
          </Text>
          <Text>
            Edit and resubmit your work according to the received feedback.
          </Text>
        </Flex>
      )
    },
    editorial: {
      heading: 'Unsubmitted',
      description: (params: StringTemplateData) =>
        `This submission was unsubmitted on ${params.dateSent} and reverted to a draft state. The author will be prompted to edit their work and resubmit.`
    },
    reviewer: { heading: 'Unsubmitted', description: '' },
    expert: { heading: '', description: '' }
  },
  rejected: {
    author: { heading: '', description: '' },
    editorial: { heading: '', description: '' },
    reviewer: { heading: 'Rejected', description: '' },
    expert: { heading: '', description: '' }
  },
  withdrawn: {
    author: { heading: '', description: '' },
    editorial: { heading: '', description: '' },
    reviewer: { heading: 'Withdrawn', description: '' },
    expert: { heading: '', description: '' }
  }
};

export function getPhaseContent(
  phase: IWorkPhase,
  role: IRoleType,
  params: StringTemplateData = {}
): {
  heading: ReactNode;
  description: ReactNode;
} {
  const phaseContent = documentPhases?.[phase]?.[role];

  if (phaseContent) {
    return {
      heading:
        typeof phaseContent.heading === 'string'
          ? phaseContent.heading
          : phaseContent.heading(params),
      description:
        typeof phaseContent.description === 'function'
          ? phaseContent.description(params)
          : phaseContent.description
    };
  }

  return {
    heading: '',
    description: ''
  };
}

export function getAssessmentPhaseContent(
  phase: IExpertPhases,
  params: StringTemplateData = {}
): {
  heading: ReactNode;
  description: ReactNode;
} {
  const phaseContent = assessmentPhaseDescription?.[phase];

  if (phaseContent) {
    return {
      heading:
        typeof phaseContent.heading === 'string'
          ? phaseContent.heading
          : phaseContent.heading(params),
      description:
        typeof phaseContent.description === 'function'
          ? phaseContent.description(params)
          : phaseContent.description
    };
  }

  return {
    heading: '',
    description: ''
  };
}
